import type { ReactNode } from 'react';

export interface DataTableColumn<T> {
  /** Chave única (também usada como path para acesso default ao valor) */
  key: keyof T & string;
  label: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Renderização customizada da célula. Recebe a row inteira. */
  render?: (row: T) => ReactNode;
  /** Aplica fonte mono (códigos) */
  code?: boolean;
  /** Aplica fonte mono + alinhamento à direita (valores monetários) */
  money?: boolean;
  /** Largura sugerida (CSS width: '120px', '20%' etc.) */
  width?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Callback ao clicar em uma linha (ativa cursor pointer + hover) */
  onRowClick?: (row: T) => void;
  /** Estado loading (5 skeletons) */
  loading?: boolean;
  /** Mensagem quando rows.length === 0 */
  empty?: ReactNode;
  /** Caption acessível para a tabela */
  caption?: string;
  className?: string;
}

/**
 * DataTable densa estilo OM, com header sticky e suporte a render customizado.
 *
 * @example
 * <DataTable
 *   columns={[
 *     { key: 'codigo', label: 'Código', code: true },
 *     { key: 'objeto', label: 'Objeto' },
 *     { key: 'valor', label: 'Valor', money: true },
 *     { key: 'status', label: 'Status', render: (r) => <Chip variant="success">{r.status}</Chip> },
 *   ]}
 *   rows={processos}
 *   onRowClick={(p) => router.push(`/processos/${p.id}`)}
 * />
 */
export default function DataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  onRowClick,
  loading = false,
  empty,
  caption,
  className,
}: DataTableProps<T>) {
  const baseTable = 'w-full border-collapse text-[13px]';
  const baseTh =
    'sticky top-0 text-left px-3 py-2 bg-[var(--bg-page)] text-[var(--fg-muted)] text-[11.5px] font-semibold uppercase tracking-[0.04em] border-b border-[var(--border)] whitespace-nowrap';
  const baseTd = 'px-3 py-2.5 border-b border-[var(--border)] align-top';
  const baseTr = onRowClick
    ? 'transition-colors duration-[120ms] cursor-pointer hover:bg-[var(--bg-hover)]'
    : 'transition-colors duration-[120ms] hover:bg-[var(--bg-hover)]';

  if (loading) {
    return (
      <table className={`${baseTable} ${className ?? ''}`}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={baseTh}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col.key} className={baseTd}>
                  <div className="h-4 w-3/4 rounded bg-[var(--gray-100)] animate-pulse" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-[var(--fg-muted)]">
        {empty ?? 'Nenhum item encontrado.'}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
    <table className={`${baseTable} ${className ?? ''}`}>
      {caption && <caption className="sr-only">{caption}</caption>}
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              scope="col"
              className={`${baseTh} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
              style={col.width ? { width: col.width } : undefined}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={i}
            className={baseTr}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {columns.map((col) => {
              const value = row[col.key];
              const content = col.render ? col.render(row) : (value as ReactNode);
              const cellClass = [
                baseTd,
                col.code && 'font-mono text-[12.5px] text-[var(--fg)] font-medium',
                col.money && 'font-mono text-right whitespace-nowrap',
                col.align === 'right' && !col.money && 'text-right',
                col.align === 'center' && 'text-center',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <td key={col.key} className={cellClass}>
                  {content}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}
